import { useCallback, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import type { Language } from '@/types'
import type { IndexedReference, RefOptionsResult } from './reference.type'
import { useListReferences, useListReferencesLoader } from './reference.hook'
import { useListOptions } from '@/services/property/option.hook'
import { indexReferencesById, referenceToOptions } from './reference.utils'

/**
 * useRefOptions – builds formatted ref options from raw references.
 *
 * Combines:
 * - `useListReferences`  → raw ReferenceInput[]
 * - `useListOptions`     → refDataByKey (id → label) for name / category / material
 *
 * Returns:
 * - `refOptions`      – select-ready options (flat or grouped by category)
 * - `refDisplayOptions` – labels for all references already loaded in scope
 * - `referencesById`  – indexed references with flattened properties for O(1) lookup
 * - `refKey`          – the property key that holds the reference code
 *
 * Unlike the legacy approach, this hook does NOT go through productAdapter
 * or create AdaptedProduct objects. Only the fields needed for option display
 * (name, category, material) are resolved via refDataByKey label lookups.
 */
export const useRefOptions = (): RefOptionsResult => {
  const t = useTranslations('Toolbar')
  const unavailableNameLabel = t('select-reference-name-unavailable')
  const locale = useLocale()
  const lang = locale.toUpperCase() as Language

  const {
    data: references,
    isPending: isReferencesPending,
    isPlaceholderData,
  } = useListReferences()
  const {
    refDataByKey,
    refKey,
    optionsByID,
    refreshListOptions,
    isPending: isOptionsPending,
  } = useListOptions(lang)

  const isPending = isReferencesPending || isOptionsPending

  const referencesById = useMemo<Record<string, IndexedReference>>(
    () => indexReferencesById(references ?? []),
    [references],
  )

  const { refOptions, refDisplayOptions } = useMemo(() => {
    const list = references ?? []
    if (!list.length || !refKey)
      return { refOptions: [], refDisplayOptions: [] }
    const params = {
      references: list,
      refKey,
      refDataByKey,
      nameOptionsById: optionsByID.name,
      unavailableNameLabel,
    }
    return {
      refOptions: referenceToOptions(params),
      refDisplayOptions: isPlaceholderData
        ? []
        : referenceToOptions({ ...params, audience: 'display' }),
    }
  }, [
    references,
    refKey,
    refDataByKey,
    optionsByID.name,
    unavailableNameLabel,
    isPlaceholderData,
  ])

  const { loadReferences } = useListReferencesLoader()
  const refreshReferenceOptions = useCallback(async () => {
    if (isOptionsPending || !refKey) return undefined
    const freshReferences = await loadReferences({ fresh: true })
    if (!freshReferences) return undefined
    const freshOptions = await refreshListOptions('name')
    if (!freshOptions) return undefined
    return {
      refOptions: referenceToOptions({
        references: freshReferences,
        refKey,
        refDataByKey: { ...refDataByKey, name: freshOptions.refDataByKey.name },
        nameOptionsById: freshOptions.optionsByID.name,
        unavailableNameLabel,
      }),
      referencesById: indexReferencesById(freshReferences),
      nameOptions: freshOptions.selectOptions.name ?? [],
      nameRefData: freshOptions.refDataByKey.name ?? {},
      deletedOptionIdsByKey: freshOptions.deletedOptionIdsByKey,
    }
  }, [
    isOptionsPending,
    loadReferences,
    refreshListOptions,
    refKey,
    refDataByKey,
    unavailableNameLabel,
  ])

  return {
    refOptions,
    refDisplayOptions,
    referencesById,
    refKey,
    isPending,
    refreshReferenceOptions,
  }
}
